import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "form__submit" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = { key: 3 }

import {computed, ComputedRef, onMounted, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import FormSelect from "@/components/forms/FormSelect.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import {FormOption, Guid} from "@/types";
import {useActivityOccurrenceService, useActivityService} from "@/inversify.config";
import {useRouter} from "vue-router";
import {notifyError, notifySuccess} from "@/notify";
import {Activity, Participant} from "@/types/entities/activity";
import {SucceededOrNotResponse} from "@/types/responses";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import Loader from "@/components/layout/Loader.vue";

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityParticipantTransferForm',
  props: {
    activityOccurrenceId: {},
    participantId: {}
  },
  async setup(__props: any) {

let __temp: any, __restore: any

const props = __props

const {t} = useI18n();

const router = useRouter()

const activityService = useActivityService()
const activityOccurenceService = useActivityOccurrenceService();

const selectedActivityId = ref("")
const activities = ref<Activity[]>((
  ([__temp,__restore] = _withAsyncContext(() => getAllActivitiesExceptCurrent())),
  __temp = await __temp,
  __restore(),
  __temp
) ?? [])
const selectedActivity = ref<Activity>({activityOccurrences: []})

const activityOccurrences = ref<ActivityOccurrence[]>(selectedActivity.value ? getActivityOccurrencesExceptCurrent() : [])
const selectedActivityOccurrence = ref<ActivityOccurrence>(activityOccurrences.value.length > 0
    ? activityOccurrences.value[0] as ActivityOccurrence
    : {} as ActivityOccurrence)
const selectedActivityOccurrenceId = ref(selectedActivityOccurrence.value.id ?? "")

const participant = ref<Participant>({reducedMobility: false});

onMounted(async () => {
  await fetchActivityOccurrenceParticipant()
})

let activitiesOptions: ComputedRef<FormOption[]> = computed(() => {
  let formOptions = activities.value
      .filter(x => x && x.id && x.nameFr)
      .map(({id, nameFr}) => ({
        name: id,
        label: nameFr
      })) as FormOption[]
  return formOptions.sortByLabel()
});

let activityOccurrencesOptions: ComputedRef<FormOption[]> = computed(() => {
  if (activityOccurrences.value.length === 0)
    return []
  return activityOccurrences.value
      .filter(x => x && x.id && x.startDate)
      .map(x => {
        let startDate = x.startDate ? x.startDate : ""
        let endDate = x.endDate ? ` ---> ${x.endDate}` : ''
        return {
          name: x.id,
          label: `${startDate}${endDate}`
        }
      }) as FormOption[];
});

const transferButtonIsDisabled = computed(() => {
  return !Guid.isValid(selectedActivityId.value) || !Guid.isValid(selectedActivityOccurrenceId.value)
})

watch(selectedActivityId, (newId) => {
  selectedActivity.value = activities.value.find(x => x.id == newId) as Activity

  activityOccurrences.value = selectedActivity.value.activityOccurrences
  if (activityOccurrences.value.length === 0)
    return
  selectedActivityOccurrence.value = activityOccurrences.value[0] as ActivityOccurrence
  selectedActivityOccurrenceId.value = activityOccurrences.value[0].id as string
})

async function fetchActivityOccurrenceParticipant() {
  let response = await activityOccurenceService.getActivityOccurrenceParticipant(props.participantId)
  if (!Guid.isValid(response.id ?? "")) {
    displayErrors(response as SucceededOrNotResponse)
    return
  }
  participant.value = response
}

async function transferParticipant() {
  let result = await activityOccurenceService.transferActivityOccurrenceParticipant(selectedActivityOccurrenceId.value, props.participantId)
  if (result?.succeeded) {
    notifySuccess(t('participants.form.transferSaveSuccess'))
    await router.push({name: "admin.children.activities.index"})
  } else {
    displayErrors(result)
    notifyError(t('participants.form.transferSaveError'))
  }
}

async function getAllActivitiesExceptCurrent() {
  const activities = await activityService.getAllActivities(true)
  // show activity if it has more than one occurrence, otherwise hide it since we don't want to transfer participant to same occurrence
  return activities.filter(x => !x.activityOccurrences.some(y => y.id == props.activityOccurrenceId) || x.activityOccurrences.length > 1)
}

function getActivityOccurrencesExceptCurrent() {
  return selectedActivity.value.activityOccurrences.filter(x => x.id != props.activityOccurrenceId)
}

const errorsMessages = ref<string[]>([])

function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.activityOccurrence')
  } else errorsMessages.value = []
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "form",
    novalidate: "",
    onSubmit: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (transferParticipant()), ["prevent"]))
  }, [
    (participant.value?.firstName)
      ? (_openBlock(), _createBlock(Fieldset, {
          key: 0,
          title: _unref(t)('participants.participant')
        }, {
          default: _withCtx(() => [
            _createElementVNode("ul", null, [
              _createElementVNode("li", null, [
                _createElementVNode("strong", null, _toDisplayString(_unref(t)('participants.firstName')) + ":", 1),
                _createTextVNode(" " + _toDisplayString(participant.value.firstName), 1)
              ]),
              _createElementVNode("li", null, [
                _createElementVNode("strong", null, _toDisplayString(_unref(t)('participants.lastName')) + ":", 1),
                _createTextVNode(" " + _toDisplayString(participant.value.lastName), 1)
              ]),
              _createElementVNode("li", null, [
                _createElementVNode("strong", null, _toDisplayString(_unref(t)('participants.email')) + ":", 1),
                _createTextVNode(" " + _toDisplayString(participant.value.emailAddress), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["title"]))
      : (_openBlock(), _createBlock(Loader, {
          key: 1,
          "is-inline": true
        })),
    (_unref(activitiesOptions).length)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createVNode(Fieldset, null, {
            default: _withCtx(() => [
              (errorsMessages.value.length)
                ? (_openBlock(), _createBlock(FormErrorsList, {
                    key: 0,
                    errorsList: errorsMessages.value,
                    title: _unref(t)('global.formErrorNotification')
                  }, null, 8, ["errorsList", "title"]))
                : _createCommentVNode("", true),
              _createVNode(FormRow, null, {
                default: _withCtx(() => [
                  _createVNode(FormSelect, {
                    modelValue: selectedActivityId.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedActivityId).value = $event)),
                    label: _unref(t)('activities.activity'),
                    options: _unref(activitiesOptions),
                    rules: [],
                    name: "activityId"
                  }, null, 8, ["modelValue", "label", "options"]),
                  (_unref(activityOccurrencesOptions).length)
                    ? (_openBlock(), _createBlock(FormSelect, {
                        key: 0,
                        modelValue: selectedActivityOccurrenceId.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedActivityOccurrenceId).value = $event)),
                        label: _unref(t)('activityOccurrences.occurrence'),
                        options: _unref(activityOccurrencesOptions),
                        rules: [],
                        name: "activityOccurrenceId"
                      }, null, 8, ["modelValue", "label", "options"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("button", {
              disabled: transferButtonIsDisabled.value,
              class: "btn btn--fullscreen",
              type: "submit"
            }, _toDisplayString(_unref(t)('global.transfer')), 9, _hoisted_2)
          ])
        ], 64))
      : (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_unref(t)('activities.noAvailableActivity')), 1))
  ], 32))
}
}

})